import React, {useEffect, useRef, useState, createRef} from "react";
import defaultStyles from "./Select.module.scss";
import {useTranslation} from "react-i18next";
import Checkbox from "../Checkbox/Checkbox";
import RadioButton from "../RadioButton/RadioButton";

export type SelectOptionType = {text: string | React.ReactNode; value: string | number | undefined};

type SelectProps = {
  value?: string | number | (string | number)[];
  onChange?: (value: string | number | (string | number)[] | undefined) => void;
  placeHolder?: string | React.ReactNode;
  options: SelectOptionType[];
  loading?: boolean;
  title?: string | React.ReactNode;
  multi?: boolean;
  styles?: SelectClasses;
  showCheckboxes?: true;
  showRadioButtons?: boolean;
  selectBoxId?: string;
  optionListId?: string;
  iconClass?: string;
};

type SelectClasses = {
  wrapper?: string;
  optionsWrapper?: string;
  selected?: string;
  option?: string;
  placeholder?: string;
};

const Select: React.FC<SelectProps> = ({
  onChange = () => {
    return;
  },
  placeHolder,
  options,
  value,
  title = "",
  multi = false,
  styles = {
    wrapper: "",
    optionsWrapper: "",
    option: "",
    selected: "",
    placeholder: "",
  },
  showCheckboxes,
  showRadioButtons = false,
  selectBoxId = "SelectBox",
  optionListId = "ListBox",
  iconClass = "icon-chevron-down",
}) => {
  const {t} = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState<SelectOptionType[]>([]);
  const [opened, setOpened] = useState(false);
  const [markedValue, setMarkedValue] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLUListElement>(null);
  const optionsRefs = useRef(Array.from({length: options.length}, () => createRef<HTMLLIElement>()));
  const openRef = useRef(false);

  useEffect(() => {
    const handleClick = (evt: MouseEvent) => {
      if (!openRef.current) {
        return;
      }
      if (!(containerRef.current === evt.target || containerRef.current?.contains(evt.target as HTMLElement))) {
        setOpened(false);
        openRef.current = false;
        setMarkedValue("");
      }
    };

    document.body.addEventListener("click", handleClick);

    return () => {
      document.body.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedOptions(options.filter((option) => option.value && value.includes(option.value)));
    } else {
      const option = options.find((_option) => _option.value === value);

      setSelectedOptions(option ? [option] : []);
    }
  }, [value, options]);

  // option click
  const onSelect = (option: SelectOptionType) => {
    onChange(option.value);
    if (!multi) {
      setOpened(false);
      openRef.current = false;
      setMarkedValue("");
    }
  };

  // is there an option, that can be cleared by 'onClear'
  const isOneOrLessOption = options.length <= 1;

  // styles handling design and behaviour
  const containerClassName = [
    defaultStyles.wrapper,
    isOneOrLessOption ? defaultStyles.unselectable : "",
    styles.wrapper,
  ].join(" ");

  return (
    <div ref={containerRef} className={containerClassName}>
      <button
        type="button"
        role="combobox"
        value="Select"
        id={selectBoxId}
        aria-controls={optionListId}
        aria-haspopup="listbox"
        aria-expanded={opened}
        onClick={() => {
          setOpened(!opened);
          openRef.current = !opened;
          setMarkedValue("");
        }}
      >
        {selectedOptions.length ? (
          <>
            {title} {multi ? "" : selectedOptions[0].text}
          </>
        ) : (
          <span className={[defaultStyles.placeholder, styles.placeholder].join(" ")}>
            {placeHolder ?? t("form.pleaseChoose")}
          </span>
        )}
        {!isOneOrLessOption && <span className={`icon icon-size--18 ${iconClass}`}>&#160;</span>}
      </button>
      {opened && options.length > 0 && (
        <ul
          role="listbox"
          id={optionListId}
          ref={optionsContainerRef}
          className={`list-style-none ${defaultStyles.optionsWrapper} ${styles.optionsWrapper ?? ""}`}
        >
          {options.map((option, index) => {
            const isSelected = selectedOptions.some((_option) => _option.value === option.value);

            return (
              <li
                ref={optionsRefs.current[index]}
                key={index}
                className={`${defaultStyles.option} ${markedValue === String(option.value) ? defaultStyles.marked : ""} ${styles.option ?? ""} ${
                  isSelected ? [defaultStyles.selected, styles.selected].join(" ") : ""
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                {multi || showCheckboxes ? (
                  <Checkbox
                    className={defaultStyles.checkbox}
                    wrapperClassName={defaultStyles.checkboxWrapper}
                    checked={isSelected}
                    color={"green"}
                    name={`${optionListId}-Checkbox`}
                    onChange={() => {
                      onSelect(option);
                    }}
                  >
                    {option.text}
                  </Checkbox>
                ) : showRadioButtons ? (
                  <RadioButton
                    value={option.value}
                    checked={isSelected}
                    name={"percentageDiscount"}
                    onChange={() => {
                      onSelect(option);
                    }}
                  >
                    {option.text}
                  </RadioButton>
                ) : (
                  <button
                    role="option"
                    autoFocus={isSelected}
                    aria-selected={isSelected}
                    onClick={() => {
                      onSelect(option);
                    }}
                  >
                    {option.text}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Select;
